/**
 * PDF annotation surface (§14).
 *
 * The handler already knew how to write highlights, notes and ink into a PDF;
 * nothing let anyone make one. This is that layer.
 *
 * ## Coordinates
 *
 * The one thing this file has to get right. A PDF's user space has its origin
 * at the **bottom left** and is measured in points; the browser's has it at the
 * **top left** and is measured in CSS pixels, scaled by the zoom and rotated by
 * whatever the page says. Converting by hand — `pdfHeight - y` and a multiply —
 * works on an unrotated page at 100% and quietly misplaces every annotation
 * anywhere else.
 *
 * So the conversion is always `viewport.convertToPdfPoint`, which pdf.js
 * derives from the same transform it rendered with. The annotation is stored in
 * PDF space, and drawn back through `convertToViewportPoint`. A stored
 * annotation therefore lands in the same place at any zoom, and lands in the
 * same place in Acrobat.
 *
 * ## Nothing is written until save
 *
 * Annotations accumulate in the model. The file on disk is untouched until the
 * user saves, and then a new file is written — §13's default, and the reason
 * the original is safe while someone experiments.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { PageViewport } from 'pdfjs-dist';
import type { PdfAnnotation, RgbColor } from '@moduviewer/handlers';
import { Icon } from '@moduviewer/design/icon';

export type AnnotationTool = 'highlight' | 'note' | 'ink' | null;

/** The palette offered for highlights and ink. */
export const ANNOTATION_COLORS: readonly (readonly [label: string, color: RgbColor])[] = [
  ['노랑', [1, 0.92, 0.23]],
  ['초록', [0.55, 0.88, 0.5]],
  ['파랑', [0.55, 0.78, 0.97]],
  ['분홍', [0.98, 0.65, 0.82]],
  ['빨강', [0.85, 0.15, 0.15]],
];

export interface AnnotationLayerProps {
  readonly pageIndex: number;
  readonly viewport: PageViewport;
  readonly annotations: readonly PdfAnnotation[];
  readonly tool: AnnotationTool;
  readonly color: RgbColor;
  readonly onAdd: (annotation: PdfAnnotation) => void;
  /** Index within the whole model, so removal does not need a search. */
  readonly onRemove: (index: number) => void;
  /** Model indices of this page's annotations, in the same order. */
  readonly indices: readonly number[];
}

const css = (color: RgbColor, alpha = 1): string =>
  `rgba(${Math.round(color[0] * 255)}, ${Math.round(color[1] * 255)}, ${Math.round(color[2] * 255)}, ${alpha})`;

export function AnnotationLayer({
  pageIndex, viewport, annotations, tool, color, onAdd, onRemove, indices,
}: AnnotationLayerProps): React.JSX.Element {
  const host = useRef<HTMLDivElement>(null);
  const [drawing, setDrawing] = useState<number[] | null>(null);
  const [noteAt, setNoteAt] = useState<{ x: number; y: number } | null>(null);

  /** Pointer position in PDF user space. */
  const toPdf = useCallback((event: { clientX: number; clientY: number }) => {
    const box = host.current?.getBoundingClientRect();
    if (!box) return null;
    const [x, y] = viewport.convertToPdfPoint(
      event.clientX - box.left, event.clientY - box.top,
    );
    return { x: x!, y: y! };
  }, [viewport]);

  /* ---- highlight, from a text selection ---- */

  /**
   * Turn the current selection into highlights.
   *
   * One annotation per client rect rather than one per selection: a selection
   * that spans three lines is three rectangles, and a single bounding box
   * around them would paint over the margin and the lines between.
   */
  const highlightSelection = useCallback(() => {
    const selection = window.getSelection();
    const box = host.current?.getBoundingClientRect();
    if (!selection || selection.isCollapsed || !box) return false;

    const range = selection.getRangeAt(0);
    if (!host.current?.parentElement?.contains(range.commonAncestorContainer)) return false;

    let added = 0;
    for (const rect of Array.from(range.getClientRects())) {
      // Zero-height rects come from collapsed inline boxes at line ends.
      if (rect.width < 1 || rect.height < 1) continue;
      const [x1, y1] = viewport.convertToPdfPoint(rect.left - box.left, rect.bottom - box.top);
      const [x2, y2] = viewport.convertToPdfPoint(rect.right - box.left, rect.top - box.top);
      onAdd({
        kind: 'highlight',
        page: pageIndex,
        rect: [Math.min(x1!, x2!), Math.min(y1!, y2!), Math.max(x1!, x2!), Math.max(y1!, y2!)],
        color,
      });
      added += 1;
    }
    if (added > 0) selection.removeAllRanges();
    return added > 0;
  }, [viewport, pageIndex, color, onAdd]);

  useEffect(() => {
    if (tool !== 'highlight') return;
    const onUp = (): void => { highlightSelection(); };
    document.addEventListener('mouseup', onUp);
    return () => document.removeEventListener('mouseup', onUp);
  }, [tool, highlightSelection]);

  /* ---- ink ---- */

  const startInk = useCallback((event: React.PointerEvent) => {
    if (tool !== 'ink') return;
    const point = toPdf(event);
    if (!point) return;
    (event.target as Element).setPointerCapture(event.pointerId);
    setDrawing([point.x, point.y]);
  }, [tool, toPdf]);

  const extendInk = useCallback((event: React.PointerEvent) => {
    if (drawing === null) return;
    const point = toPdf(event);
    if (!point) return;
    setDrawing((current) => (current === null ? current : [...current, point.x, point.y]));
  }, [drawing, toPdf]);

  const endInk = useCallback(() => {
    if (drawing === null) return;
    // Two points is the minimum that draws anything; a tap is not a stroke.
    if (drawing.length >= 4) {
      onAdd({ kind: 'ink', page: pageIndex, strokes: [drawing], color, width: 2 });
    }
    setDrawing(null);
  }, [drawing, pageIndex, color, onAdd]);

  /* ---- note ---- */

  const placeNote = useCallback((event: React.PointerEvent) => {
    if (tool !== 'note') return;
    const point = toPdf(event);
    if (point) setNoteAt(point);
  }, [tool, toPdf]);

  /* ---- drawing ---- */

  const view = (x: number, y: number): [number, number] => {
    const [vx, vy] = viewport.convertToViewportPoint(x, y);
    return [vx!, vy!];
  };

  return (
    <div
      ref={host}
      className={`pdf-annots${tool ? ` pdf-annots--${tool}` : ''}`}
      onPointerDown={(e) => { startInk(e); placeNote(e); }}
      onPointerMove={extendInk}
      onPointerUp={endInk}
      onPointerCancel={endInk}
    >
      <svg
        className="pdf-annots__svg"
        width={viewport.width}
        height={viewport.height}
        aria-hidden="true"
      >
        {annotations.map((annotation, i) => {
          const modelIndex = indices[i]!;
          if (annotation.kind === 'highlight') {
            const [x1, y1, x2, y2] = annotation.rect;
            const [ax, ay] = view(x1, y1);
            const [bx, by] = view(x2, y2);
            return (
              <rect
                key={modelIndex}
                x={Math.min(ax, bx)}
                y={Math.min(ay, by)}
                width={Math.abs(bx - ax)}
                height={Math.abs(by - ay)}
                fill={css(annotation.color, 0.38)}
                className="pdf-annot pdf-annot--highlight"
                onClick={() => onRemove(modelIndex)}
              />
            );
          }
          if (annotation.kind === 'ink') {
            return (
              <g key={modelIndex} className="pdf-annot" onClick={() => onRemove(modelIndex)}>
                {annotation.strokes.map((stroke, s) => (
                  <polyline
                    key={s}
                    points={pointsOf(stroke, view)}
                    fill="none"
                    stroke={css(annotation.color)}
                    strokeWidth={annotation.width}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                ))}
              </g>
            );
          }
          const [nx, ny] = view(annotation.x, annotation.y);
          return (
            <g
              key={modelIndex}
              className="pdf-annot pdf-annot--note"
              transform={`translate(${nx - 10} ${ny - 10})`}
              onClick={() => onRemove(modelIndex)}
            >
              <title>{annotation.contents}</title>
              <rect width="20" height="20" rx="4" fill={css([1, 0.85, 0.4])} />
              <path d="M5 7h10M5 10h10M5 13h6" stroke="rgba(0,0,0,0.55)" strokeWidth="1.4" strokeLinecap="round" />
            </g>
          );
        })}

        {drawing !== null && drawing.length >= 4 ? (
          <polyline
            points={pointsOf(drawing, view)}
            fill="none"
            stroke={css(color)}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : null}
      </svg>

      {noteAt ? (
        <NoteComposer
          at={view(noteAt.x, noteAt.y)}
          onCancel={() => setNoteAt(null)}
          onSave={(contents) => {
            if (contents.trim()) {
              onAdd({ kind: 'note', page: pageIndex, x: noteAt.x, y: noteAt.y, contents });
            }
            setNoteAt(null);
          }}
        />
      ) : null}
    </div>
  );
}

function pointsOf(
  flat: readonly number[], view: (x: number, y: number) => [number, number],
): string {
  const parts: string[] = [];
  for (let i = 0; i + 1 < flat.length; i += 2) {
    const [x, y] = view(flat[i]!, flat[i + 1]!);
    parts.push(`${x},${y}`);
  }
  return parts.join(' ');
}

function NoteComposer(
  { at, onSave, onCancel }: {
    at: readonly [number, number];
    onSave: (contents: string) => void;
    onCancel: () => void;
  },
): React.JSX.Element {
  const [text, setText] = useState('');
  return (
    <div
      className="pdf-note-composer"
      style={{ left: at[0], top: at[1] }}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <textarea
        className="pdf-note-composer__area"
        autoFocus
        value={text}
        placeholder="메모"
        aria-label="메모 내용"
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); onSave(text); }
        }}
      />
      <div className="pdf-note-composer__actions">
        <button className="btn" onClick={onCancel}>취소</button>
        <button className="btn btn--primary" onClick={() => onSave(text)}>추가</button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * toolbar
 * ------------------------------------------------------------------ */

export interface AnnotationToolbarProps {
  readonly tool: AnnotationTool;
  readonly color: RgbColor;
  readonly count: number;
  readonly onTool: (tool: AnnotationTool) => void;
  readonly onColor: (color: RgbColor) => void;
  readonly onClear: () => void;
}

export function AnnotationToolbar({
  tool, color, count, onTool, onColor, onClear,
}: AnnotationToolbarProps): React.JSX.Element {
  return (
    <div className="toolbar" role="toolbar" aria-label="주석 도구">
      {([
        ['highlight', 'highlight', '형광펜'],
        ['note', 'note', '메모'],
        ['ink', 'pen', '그리기'],
      ] as const).map(([id, icon, label]) => (
        <button
          key={id}
          className="btn tip"
          data-tip={label}
          aria-label={label}
          aria-pressed={tool === id}
          onClick={() => onTool(tool === id ? null : id)}
        >
          <Icon name={icon} size={15} />
          {label}
        </button>
      ))}

      <span className="sep" />

      <div className="swatches" role="radiogroup" aria-label="색">
        {ANNOTATION_COLORS.map(([label, value]) => (
          <button
            key={label}
            className="swatch"
            role="radio"
            aria-checked={sameColor(value, color)}
            aria-label={label}
            title={label}
            style={{ background: css(value) }}
            onClick={() => onColor(value)}
          />
        ))}
      </div>

      <span className="spacer" />

      <span className="toolbar__count tnum">주석 {count}개</span>
      {count > 0 ? (
        <button className="btn" onClick={onClear}>모두 지우기</button>
      ) : null}
    </div>
  );
}

function sameColor(a: RgbColor, b: RgbColor): boolean {
  return a[0] === b[0] && a[1] === b[1] && a[2] === b[2];
}
